import { useState, useEffect } from 'react';
import { Building2, MapPin, Phone, CreditCard, User, Receipt, Save, AlertCircle, RotateCcw, Check } from '../../icons/wp-icons';
import { firmendatenApi } from '../../api/firmendatenApi';

function FirmendatenSettings({ readOnly = false }) {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    // Grunddaten
    firmenname: '',
    // Adresse
    strasse: '',
    plz: '',
    ort: '',
    // Kontakt
    telefon: '',
    fax: '',
    email: '',
    website: '',
    // Bankverbindung
    bankName: '',
    iban: '',
    bic: '',
    // Geschäftsführung
    geschaeftsfuehrer: '',
    geschaeftsfuehrerTitel: '',
    handwerkskammer: '',
    // Steuerdaten
    ustIdNr: '',
    steuernummer: ''
  });

  useEffect(() => {
    loadFirmendaten();
  }, []);

  const loadFirmendaten = async () => {
    try {
      const data = await firmendatenApi.get();
      setFormData({
        firmenname: data.firmenname || '',
        strasse: data.strasse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        telefon: data.telefon || '',
        fax: data.fax || '',
        email: data.email || '',
        website: data.website || '',
        bankName: data.bankName || '',
        iban: data.iban || '',
        bic: data.bic || '',
        geschaeftsfuehrer: data.geschaeftsfuehrer || '',
        geschaeftsfuehrerTitel: data.geschaeftsfuehrerTitel || '',
        handwerkskammer: data.handwerkskammer || '',
        ustIdNr: data.ustIdNr || '',
        steuernummer: data.steuernummer || ''
      });
    } catch (err) {
      console.error('Fehler beim Laden der Firmendaten:', err);
      setError('Fehler beim Laden der Firmendaten');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (readOnly) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await firmendatenApi.update(formData);
      setSaved(true);
      // Trigger für Header-Update (falls implementiert)
      window.dispatchEvent(new CustomEvent('firmendaten-updated'));
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern der Firmendaten');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Firmendaten wirklich auf Standardwerte zurücksetzen?')) return;

    try {
      const data = await firmendatenApi.reset();
      setFormData({
        firmenname: data.firmenname || '',
        strasse: data.strasse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        telefon: data.telefon || '',
        fax: data.fax || '',
        email: data.email || '',
        website: data.website || '',
        bankName: data.bankName || '',
        iban: data.iban || '',
        bic: data.bic || '',
        geschaeftsfuehrer: data.geschaeftsfuehrer || '',
        geschaeftsfuehrerTitel: data.geschaeftsfuehrerTitel || '',
        handwerkskammer: data.handwerkskammer || '',
        ustIdNr: data.ustIdNr || '',
        steuernummer: data.steuernummer || ''
      });
      window.dispatchEvent(new CustomEvent('firmendaten-updated'));
    } catch (err) {
      console.error('Fehler beim Zurücksetzen:', err);
      setError('Fehler beim Zurücksetzen');
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-light rounded-lg">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Firmendaten</h2>
            <p className="text-sm text-text-muted">Stammdaten, Kontakt, Bank und Steuern</p>
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:text-text hover:bg-gray-100 rounded-button transition"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Zurücksetzen</span>
          </button>
        )}
      </div>

      <div className="space-y-8">
        {/* GRUNDDATEN */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Grunddaten
          </h3>
          <div>
            <label className="block text-xs text-text-muted mb-1">
              Firmenname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.firmenname}
              onChange={(e) => handleChange('firmenname', e.target.value)}
              placeholder="z.B. Musterfirma GmbH"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <p className="text-xs text-text-muted mt-2">
            Das Logo wird in den Design-Einstellungen verwaltet.
          </p>
        </div>

        {/* ADRESSE */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Adresse
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-text-muted mb-1">
                Straße + Hausnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.strasse}
                onChange={(e) => handleChange('strasse', e.target.value)}
                placeholder="z.B. Musterstraße 123"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">
                PLZ <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.plz}
                onChange={(e) => handleChange('plz', e.target.value)}
                placeholder="z.B. 12345"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">
                Ort <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.ort}
                onChange={(e) => handleChange('ort', e.target.value)}
                placeholder="z.B. Musterstadt"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* KONTAKT */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Kontakt
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-muted mb-1">
                Telefon <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.telefon}
                onChange={(e) => handleChange('telefon', e.target.value)}
                placeholder="z.B. 0123 - 456789"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Fax</label>
              <input
                type="text"
                value={formData.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
                placeholder="z.B. 0123 - 456780"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">
                E-Mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="z.B. info@musterfirma.de"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="z.B. https://www.musterfirma.de"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* BANKVERBINDUNG */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Bankverbindung
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-text-muted mb-1">
                Bank Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.bankName}
                onChange={(e) => handleChange('bankName', e.target.value)}
                placeholder="z.B. Musterbank AG"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">
                IBAN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.iban}
                onChange={(e) => handleChange('iban', e.target.value)}
                placeholder="z.B. DE89 3705 0299 0000 1234 56"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">
                BIC <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.bic}
                onChange={(e) => handleChange('bic', e.target.value)}
                placeholder="z.B. COKSDE33XXX"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </div>
          </div>
        </div>

        {/* GESCHÄFTSFÜHRUNG */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            Geschäftsführung
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-muted mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.geschaeftsfuehrer}
                onChange={(e) => handleChange('geschaeftsfuehrer', e.target.value)}
                placeholder="z.B. Max Mustermann"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Titel / Position</label>
              <input
                type="text"
                value={formData.geschaeftsfuehrerTitel}
                onChange={(e) => handleChange('geschaeftsfuehrerTitel', e.target.value)}
                placeholder="z.B. Geschäftsführer"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-text-muted mb-1">Handwerkskammer</label>
              <input
                type="text"
                value={formData.handwerkskammer}
                onChange={(e) => handleChange('handwerkskammer', e.target.value)}
                placeholder="z.B. Handwerkskammer Musterstadt"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* STEUERDATEN */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Steuerdaten
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-text-muted mb-1">
                UST-IdNr.
              </label>
              <input
                type="text"
                value={formData.ustIdNr}
                onChange={(e) => handleChange('ustIdNr', e.target.value)}
                placeholder="z.B. DE225400474"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Steuernummer</label>
              <input
                type="text"
                value={formData.steuernummer}
                onChange={(e) => handleChange('steuernummer', e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </div>
          </div>
        </div>

        {/* FEHLER */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* ERFOLG */}
        {saved && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            <Check className="w-4 h-4 shrink-0" />
            Firmendaten erfolgreich gespeichert
          </div>
        )}

        {/* SPEICHERN */}
        {!readOnly && (
          <div className="pt-4 border-t border-border">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Einstellungen speichern
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FirmendatenSettings;
